// Neu (SPEC 03 §11.2) — bereichsweise Tastenkürzel.
//
// useTasten(bereich, tasten, aktiv = true)
//   – EIN window-keydown-Hörer je Bereich; Bereiche: "global" (Shell), "auswertung", "dock",
//     "beleg", "pruefen", "kodieren", "buehne".
//   – ignoriert Ereignisse aus input/textarea/select/[contenteditable], AUSSER der Schlüssel ist
//     mit "!" markiert (z. B. "!Mod+Enter");
//   – ignoriert Ctrl/⌘/Alt, außer der Schlüssel nennt sie ("Mod+Enter", "Alt+ArrowLeft");
//     „Mod“ ist Ctrl ODER ⌘. Shift zählt nur, wenn der Schlüssel es nennt ("Shift+Enter") —
//     sonst ist "?" auch mit Shift ein "?", und "F" dasselbe wie "f".
//   – Esc gehört dem Ebenen-Stapel der Shell (store `escOberste`), nicht diesem Haken.
//   – Solange ein Modal oder ein modales Bodenblatt offen ist, schweigen alle Bereiche außer
//     "global": ein „u“ im offenen Dialog soll keinen Beleg als unleserlich markieren.
//   – "global" weicht jedem anderen Bereich, der dieselbe Taste belegt.
import { useEffect, useRef } from "react";

export type TastenBereich = "global" | "auswertung" | "dock" | "beleg" | "pruefen" | "kodieren" | "buehne" | (string & {});
export type Tasten = Record<string, (e: KeyboardEvent) => void>;

interface Bindung {
  imFeld: boolean;
  aktion: (e: KeyboardEvent) => void;
}

/** "!Mod+Enter" → { name: "Mod+Enter", imFeld: true }; Buchstaben klein. */
export function normalisiere(roh: string): { name: string; imFeld: boolean } {
  const imFeld = roh.startsWith("!");
  const teile = (imFeld ? roh.slice(1) : roh).split("+");
  // "+" selbst als Taste: "Shift++" oder "+" ergibt leere Teile.
  let taste = teile.pop() ?? "";
  if (taste === "" && teile.length && teile[teile.length - 1] === "") {
    teile.pop();
    taste = "+";
  }
  const mods = teile.filter(Boolean);
  const name = [...["Mod", "Alt", "Shift"].filter((m) => mods.includes(m)), taste.length === 1 ? taste.toLowerCase() : taste].join("+");
  return { name, imFeld };
}

/** Die Namen, unter denen ein Ereignis gesucht wird — der genaueste zuerst. */
export function kandidaten(e: Pick<KeyboardEvent, "key" | "ctrlKey" | "metaKey" | "altKey" | "shiftKey">): string[] {
  const taste = e.key.length === 1 ? e.key.toLowerCase() : e.key;
  const mod = e.ctrlKey || e.metaKey;
  const voll = [mod ? "Mod" : "", e.altKey ? "Alt" : "", e.shiftKey ? "Shift" : "", taste].filter(Boolean).join("+");
  const aus = [voll];
  if (e.shiftKey) aus.push([mod ? "Mod" : "", e.altKey ? "Alt" : "", taste].filter(Boolean).join("+"));
  return aus;
}

export function imEingabefeld(ziel: EventTarget | null): boolean {
  const el = ziel instanceof Element ? ziel : null;
  if (!el) return false;
  return !!el.closest('input, textarea, select, [contenteditable=""], [contenteditable="true"]');
}

function modalOffen(): boolean {
  return !!document.querySelector('.modal-wrap, .bottom-sheet[aria-modal="true"]');
}

function tabelleAus(tasten: Tasten): Map<string, Bindung> {
  const tabelle = new Map<string, Bindung>();
  for (const [roh, aktion] of Object.entries(tasten)) {
    const { name, imFeld } = normalisiere(roh);
    tabelle.set(name, { imFeld, aktion });
  }
  return tabelle;
}

/** Die passende Bindung für ein Ereignis — oder null (auch: Taste im Feld ohne „!“). */
function treffer(tabelle: Map<string, Bindung>, e: KeyboardEvent): Bindung | null {
  const feld = imEingabefeld(e.target);
  for (const k of kandidaten(e)) {
    const b = tabelle.get(k);
    if (!b) continue;
    return feld && !b.imFeld ? null : b;
  }
  return null;
}

/**
 * Alle gerade eingehängten Bereiche. Der Bereich "global" weicht jedem anderen: belegt ein
 * Bildschirm dieselbe Taste („f“ in Prüfen = Frage bestätigen, „/“ im Kodieren = Themenfilter),
 * gewinnt der Bildschirm — gleich, in welcher Reihenfolge die Hörer eingehängt wurden (ein
 * nachgeladener Bildschirm hängt seinen Hörer NACH dem der Shell ein).
 */
const eingehaengt = new Set<{ bereich: TastenBereich; ref: { current: Tasten } }>();

export function useTasten(bereich: TastenBereich, tasten: Tasten, aktiv = true): void {
  // Die Tasten über einen Ref: der Aufrufer baut sein Objekt bei jedem Zeichnen neu, der Hörer
  // soll trotzdem nur einmal je Bereich hängen und immer die frischesten Handlungen sehen.
  const ref = useRef(tasten);
  ref.current = tasten;

  useEffect(() => {
    if (!aktiv) return;
    const eintrag = { bereich, ref };
    eingehaengt.add(eintrag);
    const hoerer = (e: KeyboardEvent) => {
      if (e.defaultPrevented || e.isComposing) return;
      if (bereich !== "global" && modalOffen()) return;
      const b = treffer(tabelleAus(ref.current), e);
      if (!b) return;
      if (bereich === "global") {
        for (const x of eingehaengt) {
          if (x.bereich === "global") continue;
          if (modalOffen()) break;
          if (treffer(tabelleAus(x.ref.current), e)) return;
        }
      }
      b.aktion(e);
    };
    window.addEventListener("keydown", hoerer);
    return () => {
      eingehaengt.delete(eintrag);
      window.removeEventListener("keydown", hoerer);
    };
  }, [bereich, aktiv]);
}
